import { apiService } from './api'
import { TENANT_ENDPOINTS } from '../../api/endpoints'

/**
 * Tenant Whitelist Management Service
 *
 * Provides API methods for managing Azure AD tenant whitelists.
 * Only users with 'tenants.manage' permission can create/update/delete tenants.
 * Users with 'tenants.view' permission can list and view tenant details.
 */

// ============================================================================
// Types
// ============================================================================

interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  perPage: number
}

/**
 * Azure role to Crisis Buddy role mapping
 * Key: Azure AD app role value
 * Value: Crisis Buddy role slug
 */
export type RoleMapping = Record<string, string>

/**
 * Tenant whitelist model
 */
export interface Tenant {
  id: string
  tenantId: string // Azure AD tenant ID (UUID)
  tenantName: string
  domain: string | null // Primary domain (e.g., "example.com")
  isActive: boolean
  adminConsentGranted: boolean
  adminConsentDate: string | null // ISO date string
  roleMapping: RoleMapping | null // Azure role → Crisis Buddy role mapping
  notes: string | null
  addedById: string | null // User ID who added this tenant
  addedAt: string // ISO date string
  lastLoginAt: string | null // ISO date string
  createdAt: string // ISO date string
  updatedAt: string // ISO date string
}

/**
 * Schema for creating a new tenant whitelist entry
 * Note: Role mapping is now handled automatically via the fixed convention.
 * All Entra app roles must follow: crisisbuddy.<slug> → <slug>
 */
export interface CreateTenantData {
  tenantId: string // Azure AD tenant ID (UUID format)
  tenantName: string
  domain: string
  notes?: string
}

/**
 * Schema for updating a tenant whitelist entry
 * Note: Role mapping is now handled automatically via the fixed convention.
 * All Entra app roles must follow: crisisbuddy.<slug> → <slug>
 */
export interface UpdateTenantData {
  tenantName?: string
  domain?: string
  notes?: string
  isActive?: boolean
}

/**
 * Tenant list query parameters
 */
export interface TenantListParams {
  page?: number
  perPage?: number
  isActive?: boolean | undefined // Filter by active status
  search?: string | undefined // Search by name or domain
}

// ============================================================================
// API Response Types (backend wrapper format)
// ============================================================================

interface ApiResponse<T> {
  code: number
  message: string
  data: T
}

// ============================================================================
// API Methods
// ============================================================================

export const tenantService = {
  /**
   * Get paginated list of tenants
   * Requires 'tenants.view' permission
   */
  async list(params?: TenantListParams): Promise<PaginatedResponse<Tenant>> {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.perPage) queryParams.append('per_page', params.perPage.toString())
    if (params?.isActive !== undefined) queryParams.append('is_active', params.isActive.toString())
    if (params?.search) queryParams.append('search', params.search)

    const url = `${TENANT_ENDPOINTS.LIST}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const response = await apiService.get<ApiResponse<PaginatedResponse<Tenant>>>(url)
    return response.data
  },

  /**
   * Get single tenant by ID
   * Requires 'tenants.view' permission
   */
  async getById(tenantId: string): Promise<Tenant> {
    const response = await apiService.get<ApiResponse<{ tenant: Tenant }>>(
      TENANT_ENDPOINTS.TENANT_BY_ID(tenantId)
    )
    return response.data.tenant
  },

  /**
   * Create new tenant whitelist entry
   * Requires 'tenants.manage' permission
   */
  async create(data: CreateTenantData): Promise<Tenant> {
    const response = await apiService.post<ApiResponse<{ tenant: Tenant }>>(
      TENANT_ENDPOINTS.LIST,
      data
    )
    return response.data.tenant
  },

  /**
   * Update existing tenant
   * Requires 'tenants.manage' permission
   */
  async update(tenantId: string, data: UpdateTenantData): Promise<Tenant> {
    const response = await apiService.put<ApiResponse<{ tenant: Tenant }>>(
      TENANT_ENDPOINTS.TENANT_BY_ID(tenantId),
      data
    )
    return response.data.tenant
  },

  /**
   * Activate tenant (allow users from this tenant to login)
   * Requires 'tenants.manage' permission
   */
  async activate(tenantId: string): Promise<Tenant> {
    const response = await apiService.post<ApiResponse<{ tenant: Tenant }>>(
      TENANT_ENDPOINTS.ACTIVATE(tenantId),
      {}
    )
    return response.data.tenant
  },

  /**
   * Deactivate tenant (prevent users from this tenant from logging in)
   * Requires 'tenants.manage' permission
   */
  async deactivate(tenantId: string): Promise<Tenant> {
    const response = await apiService.post<ApiResponse<{ tenant: Tenant }>>(
      TENANT_ENDPOINTS.DEACTIVATE(tenantId),
      {}
    )
    return response.data.tenant
  },

  /**
   * Delete tenant from whitelist
   * Requires 'tenants.manage' permission
   */
  async delete(tenantId: string): Promise<void> {
    await apiService.delete<ApiResponse<void>>(TENANT_ENDPOINTS.TENANT_BY_ID(tenantId))
  },

  /**
   * Get all active tenants (no pagination)
   * Useful for dropdowns/selectors
   * Requires 'tenants.view' permission
   */
  async listActive(): Promise<Tenant[]> {
    const response = await this.list({ isActive: true, perPage: 1000 })
    return response.items
  },

  /**
   * Get Azure admin consent configuration
   * Requires 'tenants.manage' permission
   *
   * Returns Azure Client ID, callback URL, and a state token for CSRF protection
   */
  async getAdminConsentConfig(): Promise<{
    azureClientId: string
    adminConsentCallbackUrl: string
    state: string
  }> {
    const response = await apiService.get<
      ApiResponse<{
        azureClientId: string
        adminConsentCallbackUrl: string
        state: string
      }>
    >(`${TENANT_ENDPOINTS.LIST}/admin-consent-config`)
    return response.data
  },

  /**
   * Generate admin consent URL for a tenant
   * Used to grant Crisis Buddy permissions to access the tenant's user data
   *
   * The state parameter provides CSRF protection and must be included in the URL.
   * It will be validated when Azure redirects back to our callback endpoint.
   */
  generateAdminConsentUrl(
    tenantId: string,
    azureClientId: string,
    callbackUrl: string,
    state: string
  ): string {
    const params = new URLSearchParams({
      client_id: azureClientId,
      redirect_uri: callbackUrl,
      state: state,
    })
    return `https://login.microsoftonline.com/${tenantId}/adminconsent?${params.toString()}`
  },
}
